/**
 * KHO TEXTURE CHO HIỆU ỨNG — "hoành tráng" bắt đầu từ đây.
 *
 * Hai nguồn, cùng một bộ khoá:
 *   1. VẼ BẰNG CODE (luôn có, không phụ thuộc mạng/ảnh) — nét mềm, dùng blend ADD.
 *   2. ẢNH AI `art/ai/vfx-hit.png` + `art/ai/vfx-skill.png` (lưới 4×4, nền ĐEN).
 *      Nền đen + blend ADD = trong suốt, nên ảnh AI cắm vào là dùng được ngay.
 *      Thiếu ảnh ⇒ tự lùi về bản vẽ code, game không bao giờ vỡ.
 *
 * QUY ƯỚC ẢNH AI: mọi ô đều là hình SÁNG trên nền ĐEN; alpha lấy theo độ sáng
 * (luminance) để tia lửa/khói vẫn có viền mềm chứ không bị ô vuông đen.
 */

import Phaser from 'phaser';
import { chromaKeyMagenta } from '../art/ai-parts';
import { artPartsUrl, artUrl } from '../art/art-url';

export interface VfxSheet {
  /** khoá texture atlas chứa cả 16 ô */
  textureKey: string;
  /** tên frame trong atlas, theo đúng thứ tự ô 1..16 (null = ô trống) */
  cells: Array<string | null>;
  cols: number;
  rows: number;
}

export const VFX_SHEETS: Record<'hit' | 'skill', string> = {
  hit: 'art/ai/vfx-hit.png',
  skill: 'art/ai/vfx-skill.png',
};

/** Khoá texture vẽ bằng code. */
export const T = {
  ring: '__tdk_ring',
  slash: '__tdk_slash',
  star: '__tdk_star',
  dust: '__tdk_dust',
  spark: '__tdk_sparkline',
  crack: '__tdk_crack',
  flash: '__tdk_flash',
  beam: '__tdk_beam',
  glow: '__tdk_glow',
  shock: '__tdk_shockring',
} as const;

function canvas(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  return [c, c.getContext('2d') as CanvasRenderingContext2D];
}

function add(scene: Phaser.Scene, key: string, c: HTMLCanvasElement): void {
  if (!scene.textures.exists(key)) scene.textures.addCanvas(key, c);
}

/** Vòng xung kích: rỗng ở giữa, sáng ở vành, mờ dần ra ngoài. */
function ringCanvas(size: number, band: number, glow: number): HTMLCanvasElement {
  const [c, ctx] = canvas(size);
  const cx = size / 2;
  const r = size * 0.42;
  const g = ctx.createRadialGradient(cx, cx, Math.max(0, r - band - glow), cx, cx, r + band + glow);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.12)');
  g.addColorStop(0.82, 'rgba(255,255,255,1)');
  g.addColorStop(0.92, 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cx, r + band + glow, 0, Math.PI * 2);
  ctx.fill();
  return c;
}

/** Lưỡi liềm (vệt chém): hai cung lệch nhau ⇒ dày ở giữa, nhọn hai đầu. */
function slashCanvas(size: number): HTMLCanvasElement {
  const [c, ctx] = canvas(size);
  const cx = size / 2;
  const R = size * 0.46;
  const thick = size * 0.13;
  ctx.globalCompositeOperation = 'lighter';
  const g = ctx.createLinearGradient(0, 0, size, 0);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.5, 'rgba(255,255,255,1)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.strokeStyle = g;
  ctx.lineCap = 'round';
  // nhiều lớp mảnh ⇒ vệt chém có lõi sáng + viền mờ
  for (const [w, a] of [[thick, 0.25], [thick * 0.6, 0.55], [thick * 0.22, 1]] as Array<[number, number]>) {
    ctx.globalAlpha = a;
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.arc(cx, cx, R, Math.PI * 0.86, Math.PI * 2.14);
    ctx.stroke();
  }
  return c;
}

/** Sao va chạm 4 cánh + lens flare ngang. */
function starCanvas(size: number): HTMLCanvasElement {
  const [c, ctx] = canvas(size);
  const cx = size / 2;
  ctx.globalCompositeOperation = 'lighter';
  const g = ctx.createRadialGradient(cx, cx, 0, cx, cx, size * 0.3);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.3, 'rgba(255,255,255,0.6)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  // 4 cánh nhọn
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.beginPath();
  ctx.moveTo(cx, cx - size * 0.48);
  ctx.lineTo(cx + size * 0.055, cx);
  ctx.lineTo(cx, cx + size * 0.48);
  ctx.lineTo(cx - size * 0.055, cx);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx - size * 0.48, cx);
  ctx.lineTo(cx, cx + size * 0.045);
  ctx.lineTo(cx + size * 0.48, cx);
  ctx.lineTo(cx, cx - size * 0.045);
  ctx.closePath();
  ctx.fill();
  return c;
}

/** Cụm bụi/ khói: 3 khối tròn mềm chồng nhau. */
function dustCanvas(size: number): HTMLCanvasElement {
  const [c, ctx] = canvas(size);
  const blobs: Array<[number, number, number]> = [
    [0.42, 0.5, 0.34],
    [0.62, 0.44, 0.26],
    [0.55, 0.62, 0.22],
  ];
  for (const [bx, by, br] of blobs) {
    const g = ctx.createRadialGradient(size * bx, size * by, 0, size * bx, size * by, size * br);
    g.addColorStop(0, 'rgba(255,255,255,0.75)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.28)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  return c;
}

/** Vệt dài (tia lửa/ tốc độ): sáng ở đầu, nhọn ở đuôi. */
function streakCanvas(size: number): HTMLCanvasElement {
  const [c, ctx] = canvas(size);
  ctx.globalCompositeOperation = 'lighter';
  const g = ctx.createLinearGradient(0, 0, size, 0);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.35)');
  g.addColorStop(0.85, 'rgba(255,255,255,1)');
  g.addColorStop(1, 'rgba(255,255,255,0.2)');
  ctx.fillStyle = g;
  // thân vệt thon dần
  ctx.beginPath();
  ctx.moveTo(0, size / 2);
  ctx.quadraticCurveTo(size * 0.7, size / 2 - size * 0.09, size, size / 2 - size * 0.012);
  ctx.quadraticCurveTo(size * 0.7, size / 2 + size * 0.09, 0, size / 2);
  ctx.closePath();
  ctx.fill();
  return c;
}

/** Vết nứt đất: các nhánh gãy toả ra từ tâm. */
function crackCanvas(size: number): HTMLCanvasElement {
  const [c, ctx] = canvas(size);
  const cx = size / 2;
  const cy = size / 2;
  ctx.globalCompositeOperation = 'lighter';
  ctx.lineCap = 'round';
  // rng tất định để vết nứt giống nhau giữa các lần chạy (ảnh chụp so sánh được)
  let s = 12345;
  const rnd = (): number => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
  const branches = 9;
  for (let i = 0; i < branches; i++) {
    const a = (i / branches) * Math.PI * 2 + rnd() * 0.35;
    let x = cx;
    let y = cy;
    const steps = 4 + Math.floor(rnd() * 3);
    for (let k = 0; k < steps; k++) {
      const len = (size * 0.07 + rnd() * size * 0.07) * (1 - k / (steps + 2));
      const aa = a + (rnd() - 0.5) * 0.5;
      const nx = x + Math.cos(aa) * len;
      const ny = y + Math.sin(aa) * len;
      // càng ra ngoài càng mảnh và mờ
      const t = k / steps;
      ctx.globalAlpha = 0.95 - t * 0.6;
      ctx.lineWidth = Math.max(1, size * 0.028 * (1 - t * 0.7));
      ctx.strokeStyle = 'rgba(255,255,255,1)';
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(nx, ny);
      ctx.stroke();
      x = nx;
      y = ny;
    }
  }
  return c;
}

/** Loé sáng tròn mềm (dùng cho flash tại chỗ). */
function flashCanvas(size: number): HTMLCanvasElement {
  const [c, ctx] = canvas(size);
  const cx = size / 2;
  const g = ctx.createRadialGradient(cx, cx, 0, cx, cx, cx);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.75)');
  g.addColorStop(0.7, 'rgba(255,255,255,0.22)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return c;
}

/** Luồng dài tới trước (chưởng/ beam). */
function beamCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d') as CanvasRenderingContext2D;
  const g = ctx.createLinearGradient(0, 0, w, 0);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.5)');
  g.addColorStop(0.75, 'rgba(255,255,255,0.95)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(0, h / 2);
  ctx.quadraticCurveTo(w * 0.6, 0, w, h * 0.35);
  ctx.lineTo(w, h * 0.65);
  ctx.quadraticCurveTo(w * 0.6, h, 0, h / 2);
  ctx.closePath();
  ctx.fill();
  return c;
}

/** Vòng xung kích "dẹt" trên mặt đất (nhìn nghiêng). */
function shockCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d') as CanvasRenderingContext2D;
  const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.62, 'rgba(255,255,255,0.1)');
  g.addColorStop(0.85, 'rgba(255,255,255,0.95)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.ellipse(w / 2, h / 2, w / 2 - 1, h / 2 - 1, 0, 0, Math.PI * 2);
  ctx.fill();
  return c;
}

/** Vẽ toàn bộ texture code. Gọi 1 lần khi tạo sân. */
export function ensureVfxTextures(scene: Phaser.Scene): void {
  const S = 256;
  add(scene, T.ring, ringCanvas(S, S * 0.05, S * 0.04));
  add(scene, T.slash, slashCanvas(S));
  add(scene, T.star, starCanvas(S));
  add(scene, T.dust, dustCanvas(S));
  add(scene, T.spark, streakCanvas(S));
  add(scene, T.crack, crackCanvas(S));
  add(scene, T.flash, flashCanvas(S));
  add(scene, T.beam, beamCanvas(S, 96));
  add(scene, T.shock, shockCanvas(S, 96));
  if (!scene.textures.exists(T.glow)) {
    const [c, ctx] = canvas(128);
    const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
    g.addColorStop(0, 'rgba(255,255,255,0.9)');
    g.addColorStop(0.45, 'rgba(255,255,255,0.35)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
    add(scene, T.glow, c);
  }
}

// ---------------------------------------------------------------- nạp ảnh AI

const cache: Record<string, VfxSheet | null> = {};

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/**
 * Nạp 1 ảnh VFX AI (lưới 4×4, nền đen) → **MỘT** texture atlas 16 frame.
 *
 * ⚠️ BẪY ĐÃ ĐO ĐƯỢC: bản đầu tạo 16 texture riêng cho mỗi ảnh (32 texture).
 * Trong Chrome thật, mỗi `textures.addCanvas` là một lần upload GPU ⇒ nạp 2 ảnh VFX
 * mất **8,4 giây** (đo được), chiếm gần hết thời gian boot.
 * Gộp thành 1 atlas/ảnh: 1 lần đọc pixel + 1 lần upload, còn ~0,2 giây.
 *
 * Alpha = độ sáng lớn nhất của pixel (nền đen ⇒ trong suốt) để tia lửa/ khói vẫn mềm.
 */
async function loadSheet(scene: Phaser.Scene, which: 'hit' | 'skill'): Promise<VfxSheet | null> {
  const url = artUrl(VFX_SHEETS[which]);
  const img = await loadImage(url);
  if (!img) return null;
  const cols = 4;
  const rows = 4;
  const cw = Math.floor(img.width / cols);
  const ch = Math.floor(img.height / rows);

  const atlas = document.createElement('canvas');
  atlas.width = img.width;
  atlas.height = img.height;
  const ctx = atlas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
  ctx.drawImage(img, 0, 0);
  const id = ctx.getImageData(0, 0, atlas.width, atlas.height);
  const d = id.data;
  for (let i = 0; i < d.length; i += 4) {
    // độ sáng = alpha (nền đen ⇒ 0)
    d[i + 3] = Math.max(d[i], d[i + 1], d[i + 2]);
  }
  ctx.putImageData(id, 0, 0);

  const textureKey = `__tdk_aivfx_${which}`;
  if (scene.textures.exists(textureKey)) scene.textures.remove(textureKey);
  const tex = scene.textures.addCanvas(textureKey, atlas);
  if (!tex) return null;

  const cells: Array<string | null> = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const frame = `c${cells.length}`;
      tex.add(frame, 0, c * cw, r * ch, cw, ch);
      cells.push(frame);
    }
  }
  return { textureKey, cells, cols, rows };
}

export async function loadVfxSheets(scene: Phaser.Scene): Promise<Record<'hit' | 'skill', VfxSheet | null>> {
  for (const which of ['hit', 'skill'] as const) {
    if (!(which in cache)) {
      const t0 = performance.now();
      cache[which] = await loadSheet(scene, which);
      console.log(`[vfx] nạp ${VFX_SHEETS[which]} trong ${Math.round(performance.now() - t0)}ms`);
    }
  }
  return { hit: cache.hit ?? null, skill: cache.skill ?? null };
}

/**
 * BẢNG CHỌN Ô ẢNH AI THEO LOẠI HIỆU ỨNG.
 * Ô nào trống (ảnh AI không có ô đó) thì trả về null ⇒ dùng texture vẽ bằng code.
 */
export function aiCell(sheet: VfxSheet | null, index: number): { tex: string; frame: string } | null {
  if (!sheet) return null;
  const frame = sheet.cells[index];
  return frame ? { tex: sheet.textureKey, frame } : null;
}

/** Texture code mặc định cho từng loại hiệu ứng. */
export function fallbackTexture(kind: string): string {
  switch (kind) {
    case 'ring':
    case 'aura':
      return T.ring;
    case 'shock':
    case 'aoe':
      return T.shock;
    case 'slash':
      return T.slash;
    case 'impact':
    case 'hit':
      return T.star;
    case 'dust':
      return T.dust;
    case 'spark':
    case 'streak':
      return T.spark;
    case 'crack':
      return T.crack;
    case 'flash':
      return T.flash;
    case 'beam':
      return T.beam;
    case 'die':
      return T.star;
    case 'spawn':
      return T.ring;
    case 'swirl':
      return T.ring;
    default:
      return T.glow;
  }
}

/**
 * LƯỚI ẢNH 3×3 CHO ULTIMATE — nạp art riêng (máy bay · bom xăng).
 *
 * ⚠️ KHÁC `loadSheet` ở 2 điểm, KHÔNG dùng chung được:
 *   · `loadSheet` là lưới **4×4** nền **ĐEN** và lấy alpha = độ sáng;
 *   · art ultimate là lưới **3×3** nền **MAGENTA** (cùng chuẩn với `art/ai/<class>-parts.png`)
 *     ⇒ phải tách nền bằng chroma-key, nếu không ô sprite sẽ có nền hồng.
 * Dùng CHUNG `chromaKeyMagenta` với `art/ai-parts.ts` để 2 đường nạp không lệch chuẩn.
 */
export async function loadUltFxSheet(scene: Phaser.Scene, artId: string): Promise<VfxSheet | null> {
  const url = artPartsUrl(artId);
  const img = await loadImage(url);
  if (!img) return null;
  const cols = 3;
  const rows = 3;
  const cw = Math.floor(img.width / cols);
  const ch = Math.floor(img.height / rows);
  const atlas = document.createElement('canvas');
  atlas.width = img.width;
  atlas.height = img.height;
  const ctx = atlas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
  ctx.drawImage(img, 0, 0);
  const id = ctx.getImageData(0, 0, atlas.width, atlas.height);
  chromaKeyMagenta(id.data);
  ctx.putImageData(id, 0, 0);

  const textureKey = `__tdk_ultfx_${artId}`;
  if (scene.textures.exists(textureKey)) scene.textures.remove(textureKey);
  const tex = scene.textures.addCanvas(textureKey, atlas);
  if (!tex) return null;

  const cells: Array<string | null> = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const frame = `c${cells.length}`;
      tex.add(frame, 0, c * cw, r * ch, cw, ch);
      cells.push(frame);
    }
  }
  return { textureKey, cells, cols, rows };
}
